import { useState } from "react";
import { CheckCircle2, XCircle, FileDown } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";

const fmt = (n, cur = "MXN") => Number(n || 0).toLocaleString("es-MX", { style: "currency", currency: cur });
const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" }) : "");
const BADGE = {
  pending: "bg-amber-100 text-amber-900", approved: "bg-emerald-100 text-emerald-800", rejected: "bg-red-100 text-red-800",
  expired: "bg-slate-200 text-slate-700", cancelled: "bg-slate-200 text-slate-700",
};

function DecisionDialog({ quote, decision, onClose, onDecide }) {
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const approve = decision === "approve";
  async function submit() {
    setBusy(true); setError(null);
    try { await onDecide(quote.id, decision, comment.trim() || null); onClose(); }
    catch (e) { setError(e?.response?.data?.detail || "No se pudo registrar tu respuesta. Intenta de nuevo."); }
    finally { setBusy(false); }
  }
  return (
    <AlertDialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <AlertDialogContent data-testid="portal-quote-confirm">
        <AlertDialogHeader>
          <AlertDialogTitle>{approve ? "¿Autorizar la cotización?" : "¿Rechazar la cotización?"}</AlertDialogTitle>
          <AlertDialogDescription>
            {approve ? `Autorizas ${quote.number} por ${fmt(quote.total, quote.currency)} con IVA.` : `Rechazas ${quote.number}. El taller verá tu respuesta.`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Textarea rows={3} maxLength={500} value={comment} onChange={(e) => setComment(e.target.value)}
          placeholder={approve ? "Comentario para el taller (opcional)" : "¿Por qué la rechazas? (opcional)"} data-testid="portal-quote-comment" />
        {error && <div className="text-sm text-red-600" data-testid="portal-quote-error">{error}</div>}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={(e) => { e.preventDefault(); submit(); }} disabled={busy} data-testid="portal-quote-confirm-btn">
            {busy ? "Enviando…" : approve ? "Sí, autorizar" : "Sí, rechazar"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function QuoteCard({ quote, folio, vehicle, backendUrl, onAsk }) {
  const pending = quote.status === "pending";
  return (
    <div id={pending ? "cotizacion" : undefined} className={`bg-white rounded-xl p-5 scroll-mt-4 ${pending ? "border-2" : "border border-border"}`}
      style={pending ? { borderColor: "var(--tenant-primary)" } : undefined} data-testid={`portal-quote-card-${quote.id}`}>
      <div className="flex items-start justify-between gap-3 mb-1">
        <div className="text-base font-semibold" data-testid="portal-quote-number">{quote.number}</div>
        <span className={`text-xs font-medium px-2 py-1 rounded-full shrink-0 ${BADGE[quote.status]}`} data-testid={`portal-quote-status-${quote.id}`}>{quote.status_label}</span>
      </div>
      <div className="text-xs text-muted-foreground mb-3">{fmtDay(quote.date)} · Folio {folio}{vehicle ? ` · ${vehicle}` : ""}</div>
      <div className="space-y-1.5 text-sm">
        {quote.items?.map((it, i) => (
          <div key={i} className="flex justify-between gap-2">
            <span className="text-slate-700 flex-1">{it.concept} <span className="text-muted-foreground">× {it.quantity}</span></span>
            <span className="tabular-nums font-medium shrink-0">{fmt(it.total, quote.currency)}</span>
          </div>
        ))}
      </div>
      <div className="border-t border-border mt-3 pt-3 space-y-1 text-sm">
        <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span>{fmt(quote.subtotal, quote.currency)}</span></div>
        <div className="flex justify-between text-muted-foreground"><span>IVA</span><span>{fmt(quote.iva_amount, quote.currency)}</span></div>
        <div className="flex justify-between text-lg font-semibold pt-1">
          <span>Total con IVA</span>
          <span style={{ color: "var(--tenant-primary)" }} data-testid={`portal-quote-total-${quote.id}`}>{fmt(quote.total, quote.currency)}</span>
        </div>
      </div>
      {quote.decided_at && (
        <div className="text-xs text-muted-foreground mt-2">
          {quote.status === "approved" ? "Autorizada" : "Rechazada"} el {fmtDay(quote.decided_at)}{quote.decided_via === "portal" ? " desde este portal" : " por el taller"}
          {quote.customer_comment ? ` · “${quote.customer_comment}”` : ""}
        </div>
      )}
      {quote.status === "expired" && <div className="text-xs text-muted-foreground mt-2">Esta cotización venció. Pide una nueva a tu taller.</div>}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-4">
        <a href={`${backendUrl}${quote.pdf_url}`} className="inline-flex items-center justify-center gap-1.5 text-sm h-11 px-3 rounded-lg border border-border hover:bg-slate-50 transition-colors" data-testid={`portal-quote-pdf-${quote.id}`}>
          <FileDown className="h-4 w-4" /> Descargar PDF
        </a>
        {pending && (
          <>
            <button type="button" onClick={() => onAsk(quote, "approve")} className="inline-flex items-center justify-center gap-1.5 text-sm font-medium h-11 px-4 rounded-lg text-white transition-opacity hover:opacity-90" style={{ background: "var(--tenant-primary)" }} data-testid="portal-quote-approve">
              <CheckCircle2 className="h-4 w-4" /> Autorizar
            </button>
            <button type="button" onClick={() => onAsk(quote, "reject")} className="inline-flex items-center justify-center gap-1.5 text-sm h-11 px-4 rounded-lg border border-red-200 text-red-700 hover:bg-red-50 transition-colors" data-testid="portal-quote-reject">
              <XCircle className="h-4 w-4" /> Rechazar
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export function PortalQuotes({ quotes, folio, vehicle, backendUrl, onDecide }) {
  const [ask, setAsk] = useState(null);
  return (
    <div className="space-y-3" data-testid="portal-quotes">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground px-1">Cotizaciones y autorizaciones</div>
      {!quotes?.length ? (
        <div className="bg-white border border-border rounded-xl p-5 text-sm text-muted-foreground" data-testid="portal-quotes-empty">
          Aún no tienes cotizaciones. Cuando el taller te envíe una, podrás revisarla y autorizarla aquí.
        </div>
      ) : quotes.map((q) => <QuoteCard key={q.id} quote={q} folio={folio} vehicle={vehicle} backendUrl={backendUrl} onAsk={(qq, d) => setAsk({ q: qq, d })} />)}
      {ask && <DecisionDialog quote={ask.q} decision={ask.d} onClose={() => setAsk(null)} onDecide={onDecide} />}
    </div>
  );
}
